import { useEffect, useRef } from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import authService, { TOKEN_TYPES } from "../services/authService";
import { buildApiRequestUrl } from "@/utils/apiUrl";
import { useScopeParams } from "./useScopeParams";
import { toBackendTrialId } from "@/utils/trialIdMap";
export class ApiError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function is403ApiError(error: unknown): error is ApiError {
  return isApiError(error) && error.statusCode === 403;
}

type FetchHookProps = {
  endpointSuffix: string;
  extraQueryKey?: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: any;
  enabled?: boolean;
  callback?: (data: any) => void;
  dataProcessor?: (data: any) => any;
  useQueryOptions?: Omit<UseQueryOptions, "queryKey" | "queryFn">;
  endpointPrefix?: string;
};

export const useFetch = ({
  endpointSuffix = "",
  endpointPrefix,
  extraQueryKey = "",
  method = "GET",
  body,
  enabled = true,
  callback,
  dataProcessor,
  useQueryOptions,
}: FetchHookProps) => {
  const { scope, id } = useScopeParams();
  const apiId = scope === "trial" ? toBackendTrialId(id) : id;
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const queryKey = [
    `${scope}/${endpointPrefix ?? ""}/${endpointSuffix}+${apiId}${extraQueryKey}`,
    method,
    body,
  ];
  const query = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const token = authService.getToken(TOKEN_TYPES.ACCESS);
      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
        },
      };

      // Add request body for non-GET methods
      if (body && method !== "GET") {
        options.body = JSON.stringify(body);
      }

      if (token) {
        (options.headers as Record<string, string>)["Authorization"] =
          `Bearer ${token}`;
      }

      // If no endpointPrefix provided, use default data/{scope} pattern
      // Otherwise use provided prefix (for legacy endpoints that don't follow scope pattern)
      const prefix = endpointPrefix || `data/${scope}`;

      const requestUrl = buildApiRequestUrl({
        baseUrl: import.meta.env.VITE_API_URL,
        endpointPrefix: prefix,
        id,
        endpointSuffix,
      });
      const response = await fetch(requestUrl, options);

      if (!response.ok) {
        throw new ApiError(
          `HTTP error! status: ${response.status}`,
          response.status,
        );
      }

      let data = await response.json();
      if (dataProcessor) {
        data = dataProcessor(data);
      }

      return data;
    },
    enabled: enabled,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 1,
    ...useQueryOptions,
  });

  useEffect(() => {
    if (query.data && callbackRef.current) {
      callbackRef.current(query.data);
    }
  }, [query.data]);

  return {
    ...query,
    queryKey: queryKey,
  };
};
